import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { API_BASE_URL } from '../helpers/api';
import { getEstablishments, establishmentsCount, getFilteredEstablishments } from '../helpers/establishments';
import { countCategories } from '../helpers/categories';
import { eventsCount } from '../helpers/events';

const Dashboard = () => {
  const [stats, setStats] = useState({
    establishments: 0,
    categories: 0,
    events: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch statistics
      const [establishmentsRes, categoriesRes, eventsRes] = await Promise.all([
        establishmentsCount(),
        countCategories(),
        eventsCount()
      ]);

      setStats({
        establishments: establishmentsRes,
        categories: categoriesRes,
        events: eventsRes
      });

      // Fetch recent establishments
      const {data} = await getFilteredEstablishments({}, 1, 5);

      setRecentActivity(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <h2 className="mb-4">Tableau de bord</h2>

      {/* Statistics Cards */}
      <div className="row g-4 mb-4">
        <div className="col-xl-3 col-md-6">
          <div className="card bg-primary text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">Établissements</h6>
                  <h2 className="mb-0">{stats.establishments}</h2>
                </div>
                <div>
                  <i className="bi bi-building fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link to="/establishments" className="small text-white stretched-link">
                Voir les détails
              </Link>
              <i className="bi bi-chevron-right"></i>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card bg-success text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">Catégories</h6>
                  <h2 className="mb-0">{stats.categories}</h2>
                </div>
                <div>
                  <i className="bi bi-tags fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link to="/categories" className="small text-white stretched-link">
                Gérer les catégories
              </Link>
              <i className="bi bi-chevron-right"></i>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card bg-danger text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">Événements</h6>
                  <h2 className="mb-0">{stats.events}</h2>
                </div>
                <div>
                  <i className="bi bi-calendar-event fs-1 opacity-50"></i>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link to="/events" className="small text-white stretched-link">
                Gérer les événements
              </Link>
              <i className="bi bi-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header">
              <h5 className="mb-0">Activité récente</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Établissement</th>
                      <th>Catégorie</th>
                      <th>Ville</th>
                      <th>Status</th>
                      <th>Date d'ajout</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map(establishment => (
                      <tr key={establishment.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <img
                              src={API_BASE_URL + establishment.images?.[0]?.url || '/placeholder.jpg'}
                              alt=""
                              className="rounded me-2"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            />
                            <div>{establishment.name}</div>
                          </div>
                        </td>
                        <td>{establishment.category?.name}</td>
                        <td>{establishment.city}</td>
                        <td>
                          <span className={`badge bg-${establishment.is_verified ? 'success' : 'warning'}`}>
                            {establishment.is_verified ? 'Vérifié' : 'En attente'}
                          </span>
                        </td>
                        <td>
                          {new Date(establishment.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td>
                          <Link 
                            to={`/establishments/${establishment.documentId}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;